import { Fragment } from 'react';
import { sprintf } from 'sprintf-js';
import { styled } from 'styled-components';

import type { ApiListingMiniExtra } from '#/api/types/ApiListingMini';
import { ExpandButton } from '#/components/ExpandButton/ExpandButton';
import { Hyperlink } from '#/components/Hyperlink/Hyperlink';
import { Table } from '#/components/Table/Table';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useFetchOrgListings } from '#/modules/listing/orgs/hooks/useFetchOrgListings';
import { colors, cssBreakpoints, spacing } from '#/theme/theme';
import type { DatetimeString } from '#/types/TypedDateString';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { timeago } from '#/utils/string/datetime/timeago';

const StyledTable = styled(Table)`
  @media all and (max-width: ${cssBreakpoints.smDown}) {
    tbody > tr:nth-of-type(odd) {
      background-color: ${colors.backgroundGrey};
    }

    tr > td {
      display: flex;
      padding: 0 ${spacing[8]};
    }
  }
`;

type Props = {
  listingCount: number;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listings: ApiListingMiniExtra[];
  orgId: string;
  now: DatetimeString;
  trackShowMore: string;
  trackClickedListing: string;
};

export function OrgListingsTabsBody({
  listingCount,
  listingType,
  listings: listingsArg,
  orgId,
  now,
  trackShowMore,
  trackClickedListing,
}: Props) {
  const { i18n, locale } = useI18n();

  const { loading, listings, page, setPage } = useFetchOrgListings(
    listingsArg,
    listingType,
    orgId,
  );

  const hasMore = listings.length < listingCount;
  const toggleShowMore = () => {
    if (!loading) {
      trackClicked(trackShowMore, {
        listing_type: listingType,
        page: page,
      });
      setPage(page + 1);
    }
  };

  return (
    <div
      data-qa-id="view-org-page-listings"
      style={{ marginBottom: hasMore ? 10 : undefined }}
    >
      <StyledTable
        headings={[]}
        data={listings.map((listing) => [
          listing.url[locale] && (
            <Hyperlink
              key={listing.id}
              type="link"
              href={listing.url[locale]}
              onClick={() =>
                trackClicked(trackClickedListing, {
                  listing_type: listingType,
                })
              }
            >
              {listing.name}
            </Hyperlink>
          ),

          <Fragment key="location">
            {!listing.address
              ? i18n('Online/Virtual')
              : getWorkLocationString({
                  i18n,
                  remoteOptions: {
                    remoteZone: listing.remoteZone,
                    remoteCountry: listing.remoteCountry,
                    remoteState: listing.remoteState,
                  },
                  address: listing.address,
                })}
            {listing.locationType === 'REMOTE' && (
              <span style={{ marginLeft: spacing[6] }}>
                {sprintf('(%(text)s)', { text: i18n('Remote') })}
              </span>
            )}
          </Fragment>,

          i18n('Posted %(postedAt)s', {
            postedAt: timeago(listing.lastPublished, { locale, now }),
          }),
        ])}
      />

      {hasMore && (
        <ExpandButton
          qaId="view-more-org-listings"
          onClick={() => toggleShowMore()}
          open={hasMore}
        >
          {i18n('View %(count)s more', {
            count: listingCount - listings.length,
          })}
        </ExpandButton>
      )}
    </div>
  );
}
